import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

// --- CONSTANTS ---
const MUMBAI_AREAS = [
  "Bandra West", "Juhu", "Colaba", "Powai", "Worli",
  "Andheri West", "Lower Parel", "Malad", "Borivali", "Thane",
];

const AVATAR_URLS = [
  "https://lh3.googleusercontent.com/aida-public/AB6AXuD1nAkHgoVHVJjklRDLUVR5mLhswa3vLKfZmjMyxsFztW1KVtFiEH9ZL_c6CW656zwxvGNOL4c1dIfeGw5U5DzLWePyNd67ANKNpkTdRrjXXy7wgZEC4Vh5Pr4ZsZ_d_L4Im_Tx-Pp9UYb6FLxIzyVlBf_guM8yIFFbbPCjA-YZ2ekOjpKKLKgq0spgN9ZYFvFnPcGaL1TTd78uhTwe7WaedjzCM1v-Snt85WNqfj4yZitEtERfSN8X6Ok8dlKyMemMHzNf0kN3SZNu",
  "https://lh3.googleusercontent.com/aida-public/AB6AXuChPJ09Qo6Ij8RpTtATG4Pj0slMkTXeW5j_Sy3BZPDd3vUo2mYgQtoRe2hSggLmJLRUSgtskSo7VO-cRIHhYrrxtklkSnGRRJp4eCFKHtv-XaWZsgWbWcliA29GEiAPbv9NRYF2DMkjBGDxPRZfCP6jGv0082eBPCo6JM01I1RRT7kfZk_yKEUvTfP0RB_zyKK6s1gQtIktcpf-RP3EQ1zQOVrqp81JK-CuoebD4kKR2DpeTnC22Ij433neX_yE28hATPz5QnPqtK-Y",
  "https://lh3.googleusercontent.com/aida-public/AB6AXuAC4viW6ipfa3HTEY7PDHu9Tkp8wNzarRsKOUFSohtWiGOynhKChEPNxR0tnNChOjbcT6UTpeFg7CBogHSUiroOWJF7PDq5xdFIvN2L22XkF_otlZ2z3BS9FA7C-P4Mfj544bS3FJMgJIjAYDwrdcqJFuuC4e-7d5lmer1jW2IVV3rFLzzntiWdBTpaK2Fqxty7kpTa-ldh0CTplSIP9UKAoJ0xgQgoO2CtkVEHRR1tpj5Y-7ZHzTsUjInEMBAMVy27K9scVxaSQBig",
];

// --- VALIDATION SCHEMAS ---
const signupSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  phone: z.string().regex(/^[6-9]\d{9}$/, "Valid 10-digit Indian number required"),
  interest: z.enum(["user", "driver"]).default("user"),
  email: z.string().email("Enter a valid email address"),
  area: z.string().min(1, "Please select your area"),
  // user-only
  ageConfirmed: z.boolean().optional(),
  // driver-only
  vehicleType: z.string().optional(),
  hasLicense: z.boolean().optional(),
});

const loginSchema = z.object({
  phone: z.string().regex(/^[6-9]\d{9}$/, "Valid 10-digit Indian number required"),
});

export default function WaitlistDialog({
  children,
  defaultInterest = "user",
  source = "cta",
  onLoginSuccess,
  defaultEmail = "",   // pre-fill email (e.g. from newsletter form)
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("SIGNUP");
  const [step, setStep] = useState("FORM");
  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(null);

  const signupForm = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      interest: defaultInterest === "both" ? "user" : defaultInterest,
      name: "",
      phone: "",
      email: "",
      area: "",
      ageConfirmed: false,
      vehicleType: "",
      hasLicense: false,
    },
  });

  const loginForm = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { phone: "" },
  });

  const interest = signupForm.watch("interest");

  // Pre-fill email from external source (e.g. newsletter form)
  useEffect(() => {
    if (open && defaultEmail) {
      signupForm.setValue("email", defaultEmail);
    }
  }, [open, defaultEmail]);

  // --- RECAPTCHA SETUP ---
  const setupRecaptcha = async () => {
  // Check if verifier already exists and is valid - REUSE IT
  if (window.recaptchaVerifier && window._recaptchaWidgetId !== undefined) {
    console.log("♻️ Reusing existing reCAPTCHA verifier");
    return; // Don't create a new one!
  }

  // Clean up any stale verifier completely
  if (window.recaptchaVerifier) {
    try {
      console.log("🧹 Cleaning up old reCAPTCHA verifier");
      window.recaptchaVerifier.clear();
    } catch (err) {
      console.warn("⚠️ Error clearing old verifier:", err);
    }
    window.recaptchaVerifier = null;
    window._recaptchaWidgetId = undefined;
  }

  try {
    console.log("🆕 Creating new reCAPTCHA verifier");
    
    // Create new verifier
    window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
      size: "invisible",
      callback: (response) => {
        console.log("✅ reCAPTCHA solved");
      },
      "expired-callback": () => {
        console.warn("⏱️ reCAPTCHA expired");
        window.recaptchaVerifier = null;
        window._recaptchaWidgetId = undefined;
      },
    });

    // Render and store widget ID to track if it's been rendered
    window._recaptchaWidgetId = await window.recaptchaVerifier.render();
    console.log("✅ reCAPTCHA rendered with widget ID:", window._recaptchaWidgetId);
    
  } catch (error) {
    console.error("❌ Failed to setup reCAPTCHA:", error);
    window.recaptchaVerifier = null;
    window._recaptchaWidgetId = undefined;
    throw error;
  }
};

  // --- SEND OTP ---
  const handleSendOtp = async (phone) => {
    setIsLoading(true);
    try {
      await setupRecaptcha(); // must await — render() is async
      const formatPh = "+91" + phone;
      const confirmation = await signInWithPhoneNumber(auth, formatPh, window.recaptchaVerifier);
      
      setConfirmationResult(confirmation);
      setStep("OTP");
      toast.success("OTP sent!", { 
        description: `Code sent to ${formatPh}`,
        icon: <CheckCircle2 className="h-5 w-5" />
      });
      } catch (error) {
    console.error("❌ OTP Error:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
      
    // SPECIFIC FIREBASE ERROR HANDLING
      
    // 1. BILLING/QUOTA ERRORS (PRIMARY FIX FOR YOUR ISSUE)
    if (error.code === "auth/billing-not-enabled" || error.message?.toLowerCase().includes("billing")) {
      toast.error("🔥 Firebase Billing Not Enabled", { 
        description: "Phone auth requires Firebase Blaze Plan. Enable billing in Firebase Console.",
        icon: <AlertCircle className="h-5 w-5" />,
        duration: 8000,
      });
    } 
    else if (error.code === "auth/quota-exceeded") {
      toast.error("📊 Daily Quota Exceeded", { 
        description: "Free tier limit reached (10/day). Upgrade to Blaze Plan for 10,000/month free.",
        icon: <AlertCircle className="h-5 w-5" />,
        duration: 8000,
      });
    }
    // 2. RATE LIMITING
    else if (error.code === "auth/too-many-requests") {
      toast.error("⏱️ Too Many Requests", { 
        description: "Please wait a few minutes before trying again.",
        icon: <AlertCircle className="h-5 w-5" />
      });
    }
    // 3. INVALID PHONE
    else if (error.code === "auth/invalid-phone-number") {
      toast.error("📞 Invalid Phone Number", {
        description: "Please check the number format and try again.",
        icon: <AlertCircle className="h-5 w-5" />
      });
    }
    // 4. RECAPTCHA ERRORS
    else if (error.code === "auth/captcha-check-failed") {
      toast.error("🤖 reCAPTCHA Failed", {
        description: "Please reload the page and try again.",
        icon: <AlertCircle className="h-5 w-5" />
      });
      // Force complete cleanup on reCAPTCHA failure
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch {}
        window.recaptchaVerifier = null;
        window._recaptchaWidgetId = undefined;
      }
    }
    // 5. NETWORK ERRORS
    else if (error.code === "auth/network-request-failed") {
      toast.error("🌐 Network Error", {
        description: "Please check your internet connection.",
        icon: <AlertCircle className="h-5 w-5" />
      });
    }
    // 6. GENERIC FALLBACK
    else {
      toast.error("❌ Failed to Send OTP", { 
        description: error.code || error.message || "Please try again later.",
        icon: <AlertCircle className="h-5 w-5" />,
        duration: 6000,
      });
    }
} finally {
      setIsLoading(false);
    }
  };

  const onSignupSubmit = (values) => {
    setFormData(values);
    handleSendOtp(values.phone);
  };

  const onLoginSubmit = (values) => {
    setFormData({ phone: values.phone });
    handleSendOtp(values.phone);
  };

  // --- VERIFY OTP ---
  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error("Invalid OTP", { 
        description: "Please enter 6 digits.",
        icon: <AlertCircle className="h-5 w-5" />
      });
      return;
    }

    setIsLoading(true);

    try {
      // 1. Verify with Firebase
      await confirmationResult.confirm(otp);

      let userData = null;

      if (mode === "SIGNUP") {
        // SIGNUP: POST to backend
        const response = await api.joinWaitlist({
          ...formData,
          source: source,
            role: formData.interest,
            phoneVerified: true,
            timestamp: new Date().toISOString(),
          
        });

        if (!response.ok) {
          const errorData = await response.json();
          if (response.status === 400 && errorData.detail === "User already registered") {
            toast.info("You're already on the waitlist!", {
              description: "Switching to login...",
            });
            setMode("LOGIN");
            setStep("FORM");
            setOtp("");
            return;
          }
          throw new Error(errorData.message || "Registration failed");
        }

        userData = {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          area: formData.area,
          role: formData.interest,
        };

        const roleLabel = formData.interest === "driver" ? "Fleet application received!" : "You're on the waitlist.";
        toast.success("Welcome to Liquorit!", {
          description: roleLabel,
          icon: <CheckCircle2 className="h-5 w-5" />
        });

      } else {
        // LOGIN: GET from backend
        const response = await api.getUser(formData.phone);

        if (response.ok) {
          userData = await response.json();
          toast.success(`Welcome back, ${userData.name}!`, {
            icon: <CheckCircle2 className="h-5 w-5" />
          });
        } else if (response.status === 404) {
          toast.error("Account not found", {
            description: "Please sign up first.",
            icon: <AlertCircle className="h-5 w-5" />
          });
          setMode("SIGNUP");
          setStep("FORM");
          setOtp("");
          return;
        } else {
          throw new Error("Login failed");
        }
      }

      // Success: notify parent & close
      if (onLoginSuccess && userData) {
        onLoginSuccess(userData);
      }

      handleOpenChange(false);

    } catch (error) {
      console.error("Verification error:", error);

      if (error.code === "auth/invalid-verification-code") {
        toast.error("Wrong OTP", { 
          description: "Please check the code.",
          icon: <AlertCircle className="h-5 w-5" />
        });
      } else if (error.code === "auth/code-expired") {
        toast.error("OTP expired", { 
          description: "Please request a new code.",
          icon: <AlertCircle className="h-5 w-5" />
        });
        setStep("FORM");
        setOtp("");
      } else {
        toast.error("Verification failed", { 
          description: error.message || "Please try again.",
          icon: <AlertCircle className="h-5 w-5" />
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // --- RESEND ---
  const handleResend = async () => {
  if (!formData?.phone) return;
  
  console.log("🔄 Resending OTP...");
  setOtp("");
  
  // Force complete cleanup before resending
  if (window.recaptchaVerifier) {
    try {
      window.recaptchaVerifier.clear();
    } catch {}
    window.recaptchaVerifier = null;
    window._recaptchaWidgetId = undefined; // Also clear widget ID
  }
  
  await handleSendOtp(formData.phone);
};

  const handleOpenChange = (isOpen) => {
  setOpen(isOpen);
  if (!isOpen) {
    console.log("🧹 Cleaning up dialog state...");
    setStep("FORM");
    setMode("SIGNUP");
    setOtp("");
    setFormData(null);
    signupForm.reset();
    loginForm.reset();
    
    // Complete cleanup including widget ID
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch {}
      window.recaptchaVerifier = null;
      window._recaptchaWidgetId = undefined;
    }
  }
};
return (
    <>
      {/* reCAPTCHA MUST live outside the Dialog Portal so it is never unmounted */}
      <div id="recaptcha-container"></div>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>{children}</DialogTrigger>

        <DialogContent className="premium-scroll w-[95vw] sm:w-full sm:max-w-[560px] max-h-[90dvh] overflow-y-auto border-white/10 bg-[#0B1220]/90 text-white backdrop-blur-2xl p-4 sm:p-6 gap-0 animate-in fade-in zoom-in-95 duration-300">
          <DialogHeader>
            <DialogTitle className="text-xl md:text-2xl transition-all duration-300">
              {step === "OTP"
                ? "Verify your phone"
                : mode === "SIGNUP"
                  ? "Get early access to Liquorit"
                  : "Welcome back"}
            </DialogTitle>
            <DialogDescription className="text-white/70 transition-all duration-300">
              {step === "OTP"
                ? `Enter code sent to +91 ${formData?.phone}`
                : mode === "SIGNUP"
                  ? interest === "driver" ? "Apply as a delivery partner in Mumbai." : "Secure your spot for our upcoming launch."
                  : "Login with your registered phone number."}
            </DialogDescription>
          </DialogHeader>

          {step === "FORM" ? (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              {mode === "SIGNUP" ? (
                /* --- SIGNUP FORM --- */
                <form onSubmit={signupForm.handleSubmit(onSignupSubmit)} className="mt-4 space-y-5">

                  {/* ── SOCIAL PROOF HEADER ── */}
                  <div className="flex items-center gap-3 rounded-xl px-4 py-3" style={{background:"rgba(197,160,89,0.06)",border:"1px solid rgba(197,160,89,0.15)"}}>
                    <div className="flex -space-x-2 flex-shrink-0">
                      {AVATAR_URLS.map((src, i) => (
                        <img key={i} src={src} alt="" style={{width:28,height:28,borderRadius:"50%",border:"2px solid #0B1220",objectFit:"cover"}} />
                      ))}
                    </div>
                    <p className="text-xs leading-snug" style={{color:"#C5A059"}}>
                      Join the <strong>Mumbaikars</strong> already waiting
                    </p>
                  </div>

                  {/* ── TAB: User / Driver ── */}
                  <Tabs value={interest} onValueChange={(v) => signupForm.setValue("interest", v)}>
                    <TabsList className="grid w-full grid-cols-2 h-11 sm:h-12 bg-white/5 p-1">
                      <TabsTrigger value="user" className="data-[state=active]:bg-white/10 transition-all duration-300 data-[state=active]:text-[#C5A059]">
                        User
                      </TabsTrigger>
                      <TabsTrigger value="driver" className="data-[state=active]:bg-white/10 transition-all duration-300 data-[state=active]:text-[#C5A059]">
                        Driver
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value={interest} className="mt-5 space-y-4 animate-in fade-in duration-300">

                      {/* Name + Phone — both */}
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-white/85">Full Name <span className="text-red-400">*</span></Label>
                          <Input
                            id="name"
                            placeholder="Rahul Sharma"
                            className="h-11 sm:h-12 rounded-xl border-white/10 bg-white/[0.03] text-base text-white transition-all focus-visible:ring-[#C5A059] focus-visible:border-[#C5A059]/50 hover:bg-white/[0.05]"
                            {...signupForm.register("name")}
                          />
                          {signupForm.formState.errors.name && (
                            <p className="text-sm text-red-400 animate-in fade-in">{signupForm.formState.errors.name.message}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-white/85">Phone Number <span className="text-red-400">*</span></Label>
                          <Input
                            id="phone"
                            type="tel"
                            placeholder="9876543210"
                            maxLength={10}
                            className="h-11 sm:h-12 rounded-xl border-white/10 bg-white/[0.03] text-base text-white transition-all focus-visible:ring-[#C5A059] focus-visible:border-[#C5A059]/50 hover:bg-white/[0.05]"
                            {...signupForm.register("phone")}
                          />
                          {signupForm.formState.errors.phone && (
                            <p className="text-sm text-red-400 animate-in fade-in">{signupForm.formState.errors.phone.message}</p>
                          )}
                        </div>
                      </div>

                      {/* Email — both */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-white/85">Email <span className="text-red-400">*</span></Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          className="h-11 sm:h-12 rounded-xl border-white/10 bg-white/[0.03] text-base text-white transition-all focus-visible:ring-[#C5A059] focus-visible:border-[#C5A059]/50 hover:bg-white/[0.05]"
                          {...signupForm.register("email")}
                        />
                        {signupForm.formState.errors.email && (
                          <p className="text-sm text-red-400 animate-in fade-in">{signupForm.formState.errors.email.message}</p>
                        )}
                      </div>

                      {/* Area — both */}
                      <div className="space-y-2">
                        <Label htmlFor="area" className="text-white/85">Area in Mumbai <span className="text-red-400">*</span></Label>
                        <Select value={signupForm.watch("area")} onValueChange={(v) => signupForm.setValue("area", v)}>
                          <SelectTrigger id="area" className="h-11 sm:h-12 rounded-xl border-white/10 bg-white/[0.03] text-base text-white transition-all focus:ring-[#C5A059] hover:bg-white/[0.05]">
                            <SelectValue placeholder="Choose your neighbourhood" />
                          </SelectTrigger>
                          <SelectContent className="border-white/10 bg-[#0B1220] text-white">
                            {MUMBAI_AREAS.map((a) => (
                              <SelectItem key={a} value={a} className="focus:bg-white/10 cursor-pointer">{a}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {signupForm.formState.errors.area && (
                          <p className="text-sm text-red-400 animate-in fade-in">{signupForm.formState.errors.area.message}</p>
                        )}
                      </div>

                      {/* USER-ONLY: Age confirmation */}
                      {interest === "user" && (
                        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                          <label
                            className="flex items-start gap-3 cursor-pointer rounded-xl px-4 py-3 transition-all hover:bg-white/[0.03]"
                            style={{border:`1px solid ${signupForm.watch("ageConfirmed") ? "rgba(197,160,89,0.4)" : "rgba(255,255,255,0.08)"}`}}
                          >
                            <div className="relative flex-shrink-0 mt-0.5">
                              <input
                                type="checkbox"
                                {...signupForm.register("ageConfirmed")}
                                style={{
                                  width:20, height:20,
                                  appearance:"none",
                                  borderWidth:2, borderStyle:"solid",
                                  borderColor: signupForm.watch("ageConfirmed") ? "#C5A059" : "rgba(255,255,255,0.25)",
                                  background: signupForm.watch("ageConfirmed") ? "#C5A059" : "transparent",
                                  borderRadius:4, cursor:"pointer",
                                  transition:"all 0.2s", display:"block",
                                }}
                              />
                              {signupForm.watch("ageConfirmed") && (
                                <svg className="absolute inset-0 m-auto pointer-events-none" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                  <path d="M2 6l3 3 5-5" stroke="#0A0709" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </div>
                            <div>
                              <span className="text-sm font-medium" style={{color:"#E8DDD5"}}>
                                I confirm I am <strong style={{color:"#C5A059"}}>21 years or older</strong>
                              </span>
                              <p className="text-xs mt-0.5" style={{color:"rgba(255,255,255,0.4)"}}>
                                Legal drinking age in Maharashtra
                              </p>
                            </div>
                          </label>
                        </div>
                      )}

                      {/* DRIVER-ONLY: Vehicle + License */}
                      {interest === "driver" && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                          {/* Vehicle type */}
                          <div className="space-y-2">
                            <Label className="text-white/85">Vehicle Type <span className="text-red-400">*</span></Label>
                            <div className="grid grid-cols-2 gap-3">
                              {["Bike", "Scooter"].map((v) => (
                                <label
                                  key={v}
                                  className="flex items-center gap-2.5 cursor-pointer rounded-xl px-4 py-3 transition-all"
                                  style={{
                                    border:`1px solid ${signupForm.watch("vehicleType") === v ? "rgba(197,160,89,0.5)" : "rgba(255,255,255,0.08)"}`,
                                    background: signupForm.watch("vehicleType") === v ? "rgba(197,160,89,0.07)" : "transparent",
                                  }}
                                >
                                  <input
                                    type="radio"
                                    value={v}
                                    {...signupForm.register("vehicleType")}
                                    style={{accentColor:"#C5A059", width:16, height:16}}
                                  />
                                  <span className="text-sm font-medium text-white">{v}</span>
                                </label>
                              ))}
                            </div>
                          </div>

                          {/* Valid driving licence */}
                          <label
                            className="flex items-start gap-3 cursor-pointer rounded-xl px-4 py-3 transition-all hover:bg-white/[0.03]"
                            style={{border:`1px solid ${signupForm.watch("hasLicense") ? "rgba(197,160,89,0.4)" : "rgba(255,255,255,0.08)"}`}}
                          >
                            <div className="relative flex-shrink-0 mt-0.5">
                              <input
                                type="checkbox"
                                {...signupForm.register("hasLicense")}
                                style={{
                                  width:20, height:20,
                                  appearance:"none",
                                  borderWidth:2, borderStyle:"solid",
                                  borderColor: signupForm.watch("hasLicense") ? "#C5A059" : "rgba(255,255,255,0.25)",
                                  background: signupForm.watch("hasLicense") ? "#C5A059" : "transparent",
                                  borderRadius:4, cursor:"pointer",
                                  transition:"all 0.2s", display:"block",
                                }}
                              />
                              {signupForm.watch("hasLicense") && (
                                <svg className="absolute inset-0 m-auto pointer-events-none" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                  <path d="M2 6l3 3 5-5" stroke="#0A0709" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </div>
                            <div>
                              <span className="text-sm font-medium" style={{color:"#E8DDD5"}}>
                                I have a valid driving licence
                              </span>
                              <p className="text-xs mt-0.5" style={{color:"rgba(255,255,255,0.4)"}}>
                                Required to go live on the platform
                              </p>
                            </div>
                          </label>
                        </div>
                      )}

                      <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 hover:bg-white/[0.05] hover:border-[#C5A059]/20 transition-all">
                        <p className="text-sm text-white/80">We'll send an OTP to verify your phone number.</p>
                      </div>

                    </TabsContent>
                  </Tabs>

                  {/* Footer */}
                  <div className="mt-4 flex flex-col-reverse items-center justify-between gap-4 border-t border-white/5 pt-5 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => setMode("LOGIN")}
                      className="text-sm text-white/50 hover:text-white transition-colors"
                    >
                      Already have an account?{" "}
                      <span className="font-medium text-[#C5A059] underline decoration-[#C5A059]/40 underline-offset-4 hover:decoration-[#C5A059] transition-all">
                        Login
                      </span>
                    </button>
                    <div className="flex w-full gap-3 sm:w-auto">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 h-12 rounded-xl border-white/10 bg-transparent text-white hover:bg-white/5 active:scale-95 sm:flex-none transition-all"
                        onClick={() => signupForm.reset()}
                        disabled={isLoading}
                      >
                        Clear
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 h-12 rounded-xl bg-[#7C3AED] px-8 font-medium text-white hover:bg-[#6D28D9] hover:shadow-lg hover:shadow-[#7C3AED]/20 disabled:opacity-50 active:scale-95 sm:flex-none transition-all"
                        disabled={isLoading}
                      >
                        {isLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin"/>Sending...</>) : ("Send OTP")}
                      </Button>
                    </div>
                  </div>

                </form>
              ) : (
                /* --- LOGIN FORM --- */
                <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="mt-4 space-y-4 sm:space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="login-phone" className="text-white/85 transition-colors">
                      Phone Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="login-phone"
                      type="tel"
                      placeholder="9876543210"
                      maxLength={10}
                      className="h-11 sm:h-12 rounded-xl border-white/10 bg-white/[0.03] text-base text-white transition-all duration-300 focus-visible:ring-[#C5A059] focus-visible:border-[#C5A059]/50 hover:bg-white/[0.05]"
                      {...loginForm.register("phone")}
                    />
                    {loginForm.formState.errors.phone && (
                      <p className="text-sm text-red-400 animate-in fade-in slide-in-from-top-1 duration-200">
                        {loginForm.formState.errors.phone.message}
                      </p>
                    )}
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-all duration-300 hover:bg-white/[0.05] hover:border-[#C5A059]/20">
                    <p className="text-sm text-white/80">
                      We'll send a verification code to your registered number.
                    </p>
                  </div>

                  {/* --- NEW FOOTER LAYOUT (LOGIN) --- */}
                  <div className="mt-8 flex flex-col-reverse items-center justify-between gap-4 border-t border-white/5 pt-6 sm:flex-row pb-2 sm:pb-0">
                    {/* Left: Signup Link */}
                    <button
                      type="button"
                      onClick={() => setMode("SIGNUP")}
                      className="text-sm text-white/50 transition-colors hover:text-white"
                    >
                      New to Liquorit?{" "}
                      <span className="font-medium text-[#C5A059] underline decoration-[#C5A059]/40 underline-offset-4 transition-all hover:decoration-[#C5A059]">
                        Join Waitlist
                      </span>
                    </button>

                    {/* Right: Action Button */}
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-12 rounded-xl bg-[#7C3AED] px-8 font-medium text-white transition-all duration-300 hover:bg-[#6D28D9] hover:shadow-lg hover:shadow-[#7C3AED]/20 active:scale-95 sm:w-auto"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Send Login Code"
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            /* --- OTP VERIFICATION --- */
            <div className="mt-2 space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-white/85 transition-colors">
                  Enter 6-digit code
                </Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  placeholder="123456"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  className="h-14 rounded-xl border-white/10 bg-white/[0.03] text-center text-2xl tracking-widest text-white transition-all duration-300 focus-visible:ring-[#C5A059] focus-visible:border-[#C5A059]/50 hover:bg-white/[0.05]"
                  autoFocus
                />
                {otp.length > 0 && (
                  <div className="flex justify-center">
                    <div className="flex gap-2">
                      {[...Array(6)].map((_, i) => (
                        <div
                          key={i}
                          className={`h-2 w-2 rounded-full transition-all duration-300 ${i < otp.length
                              ? 'bg-[#C5A059] scale-110'
                              : 'bg-white/20'
                            }`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-all duration-300 hover:bg-white/[0.05] hover:border-[#C5A059]/20">
                <p className="text-sm text-white/80">
                  Didn't receive the code?{" "}
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={isLoading}
                    className="font-medium text-[#C5A059] transition-all duration-300 hover:underline hover:text-[#C5A059] disabled:opacity-50"
                  >
                    Resend OTP
                  </button>
                </p>
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 sm:h-12 rounded-xl border-white/15 bg-transparent text-white transition-all duration-300 hover:bg-white/5 hover:border-white/30 active:scale-95"
                  onClick={() => {
                    setStep("FORM");
                    setOtp("");
                  }}
                  disabled={isLoading}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleVerifyOtp}
                  className="h-11 sm:h-12 rounded-xl bg-[#7C3AED] px-6 font-medium text-white transition-all duration-300 hover:bg-[#6D28D9] hover:shadow-lg hover:shadow-[#7C3AED]/20 disabled:opacity-50 active:scale-95"
                  disabled={isLoading || otp.length !== 6}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify & Join"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}